import { useEffect, useMemo, useState } from "react";
import { api, formatDateTime } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Calendar, Plus, Loader2, Save, Trash2, MapPin, Bell, Copy, Globe, ChevronLeft, ChevronRight, ArrowLeft, CheckCircle2, XCircle, Clock as ClockIcon, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { DateTimePicker } from "@/components/DateTimePicker";

const TYPE_OPTIONS = [
  { v: "appointment", l: "Appuntamento", color: "blue" },
  { v: "call", l: "Telefonata", color: "violet" },
  { v: "reminder", l: "Promemoria", color: "amber" },
  { v: "task", l: "Attività interna", color: "slate" },
];
const REMINDER_OPTIONS = [
  { v: "", l: "Nessuno" },
  { v: 15, l: "15 minuti prima" },
  { v: 30, l: "30 minuti prima" },
  { v: 60, l: "1 ora prima" },
  { v: 120, l: "2 ore prima" },
  { v: 1440, l: "1 giorno prima" },
];
const STATUS_OPTIONS = [
  { v: "pending", l: "📅 Da fare", icon: ClockIcon, cls: "bg-slate-100 text-slate-700" },
  { v: "done", l: "✓ Concluso", icon: CheckCircle2, cls: "bg-emerald-100 text-emerald-800" },
  { v: "postponed", l: "↻ Spostato", icon: ClockIcon, cls: "bg-amber-100 text-amber-800" },
  { v: "cancelled", l: "✗ Annullato", icon: XCircle, cls: "bg-rose-100 text-rose-700" },
];
const OUTCOME_SUGGESTIONS = [
  "Polizza firmata",
  "Preventivo accettato",
  "Da richiamare",
  "Cliente non interessato",
  "Documenti mancanti",
  "In valutazione",
];
const COLOR_CLASS = {
  blue: "bg-blue-100 text-blue-800 border-blue-300",
  violet: "bg-violet-100 text-violet-800 border-violet-300",
  amber: "bg-amber-100 text-amber-800 border-amber-300",
  green: "bg-emerald-100 text-emerald-800 border-emerald-300",
  rose: "bg-rose-100 text-rose-800 border-rose-300",
  slate: "bg-slate-100 text-slate-700 border-slate-300",
};

const fmtDateInput = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Chiave giorno LOCALE (YYYY-MM-DD) da ISO/Date — evita shift di fuso orario
const localDateKey = (input) => {
  const d = input instanceof Date ? input : new Date(input);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Ora locale HH:MM da ISO — evita shift di fuso orario
const localTimeHM = (input) => {
  const d = input instanceof Date ? input : new Date(input);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59);

export default function Agenda() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("month"); // "month" | "list"
  const [cursor, setCursor] = useState(new Date());
  const [edit, setEdit] = useState(null); // form
  const [busy, setBusy] = useState(false);
  const [icalData, setIcalData] = useState(null);
  // OTP per rigenerare il token iCal
  const [otpOpen, setOtpOpen] = useState(false);
  const [otpStep, setOtpStep] = useState("idle"); // "idle" | "sent"
  const [otpCode, setOtpCode] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);
  const [otpRecipient, setOtpRecipient] = useState("");
  // 🆕 Stato collegamento Google Calendar per mostrare badge di sincronia
  const [gcalConnected, setGcalConnected] = useState(false);
  const [gcalPulling, setGcalPulling] = useState(false);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/oauth/calendar/status"); setGcalConnected(!!data?.connected); }
      catch { /* silent */ }
    })();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const start = startOfMonth(cursor).toISOString();
      const end = endOfMonth(cursor).toISOString();
      const { data } = await api.get(`/agenda/events?start=${start}&end=${end}`);
      setEvents(data || []);
    } catch (e) { toast.error("Errore caricamento agenda"); }
    finally { setLoading(false); }
  };

  const loadIcal = async () => {
    try {
      const { data } = await api.get("/agenda/ical-token");
      setIcalData(data);
    } catch { /* silent */ }
  };

  useEffect(() => { load(); }, [cursor]);
  useEffect(() => { loadIcal(); }, []);

  const openCreate = (presetDate) => {
    const start = presetDate ? new Date(presetDate) : new Date();
    start.setHours(start.getHours() + 1, 0, 0, 0);
    const end = new Date(start.getTime() + 60 * 60 * 1000);
    setEdit({
      id: null, title: "", description: "", type: "appointment",
      start_at: fmtDateInput(start), end_at: fmtDateInput(end),
      all_day: false, location: "", color: "blue", reminder_minutes_before: 60,
      status: "pending", outcome: "",
    });
  };
  const openEdit = (ev) => {
    setEdit({
      id: ev.id, title: ev.title, description: ev.description || "",
      type: ev.type, start_at: fmtDateInput(new Date(ev.start_at)),
      end_at: fmtDateInput(new Date(ev.end_at)),
      all_day: !!ev.all_day, location: ev.location || "",
      color: ev.color || "blue", reminder_minutes_before: ev.reminder_minutes_before ?? "",
      status: ev.status || "pending", outcome: ev.outcome || "",
    });
  };

  const save = async () => {
    if (!edit.title.trim()) { toast.error("Titolo obbligatorio"); return; }
    setBusy(true);
    try {
      const payload = {
        title: edit.title.trim(),
        description: edit.description,
        type: edit.type,
        start_at: new Date(edit.start_at).toISOString(),
        end_at: new Date(edit.end_at).toISOString(),
        all_day: !!edit.all_day,
        location: edit.location,
        color: edit.color,
        reminder_minutes_before: edit.reminder_minutes_before === "" ? null : Number(edit.reminder_minutes_before),
        status: edit.status || "pending",
        outcome: (edit.outcome || "").trim() || null,
      };
      if (edit.id) await api.put(`/agenda/events/${edit.id}`, payload);
      else await api.post("/agenda/events", payload);
      toast.success(edit.id ? "Evento aggiornato" : "Evento creato");
      setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!edit?.id) return;
    if (!window.confirm(`Eliminare "${edit.title}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/agenda/events/${edit.id}`);
      toast.success("Evento eliminato"); setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  // 🆕 Pull manuale eventi da Google Calendar → Agenda
  const pullFromGoogle = async () => {
    setGcalPulling(true);
    try {
      const { data } = await api.post("/oauth/calendar/pull");
      const c = data?.created || 0, u = data?.updated || 0, d = data?.deleted || 0, m = data?.matched || 0;
      if (c + u + d === 0) {
        toast.success("Agenda già sincronizzata con Google");
      } else {
        const matchInfo = m > 0 ? ` · ${m} abbinati a clienti` : "";
        toast.success(`Sincronizzato: +${c} nuovi · ${u} aggiornati · ${d} rimossi${matchInfo}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore sincronizzazione");
    } finally {
      setGcalPulling(false);
    }
  };

  const regenIcal = async () => {
    if (!window.confirm("Rigenerando il link, quello attuale smetterà di funzionare e dovrai re-sottoscrivere l'agenda sui dispositivi collegati. Procedere?")) return;
    setOtpOpen(true);
    setOtpStep("idle");
    setOtpCode("");
    await requestRegenOtp();
  };

  const createIcalFirstTime = async () => {
    try {
      const { data } = await api.post("/agenda/ical-token/regenerate");
      setIcalData(data);
      toast.success("Link iCal creato — usa il pulsante per subscriverlo a Google/Outlook/Apple");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const requestRegenOtp = async () => {
    setOtpBusy(true);
    try {
      const { data } = await api.post("/agenda/sensitive-otp/request");
      setOtpRecipient(data.recipient_masked || "");
      setOtpStep("sent");
      toast.success(`Codice OTP inviato a ${data.recipient_masked}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio codice");
    } finally {
      setOtpBusy(false);
    }
  };

  const verifyAndRegen = async () => {
    if (!/^\d{6}$/.test(otpCode)) { toast.error("Codice di 6 cifre"); return; }
    setOtpBusy(true);
    try {
      const { data: v } = await api.post("/agenda/sensitive-otp/verify", { code: otpCode });
      const { data } = await api.post("/agenda/ical-token/regenerate", {}, {
        headers: { "X-Agenda-Otp-Token": v.sensitive_token },
      });
      setIcalData(data);
      setOtpOpen(false); setOtpStep("idle"); setOtpCode("");
      toast.success("Nuovo link iCal generato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setOtpBusy(false);
    }
  };

  // Build month grid
  const monthGrid = useMemo(() => {
    const first = startOfMonth(cursor);
    const last = endOfMonth(cursor);
    const offset = (first.getDay() + 6) % 7; // settimana inizia lunedì
    const days = [];
    for (let i = 0; i < offset; i++) days.push(null);
    for (let d = 1; d <= last.getDate(); d++) days.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    while (days.length % 7 !== 0) days.push(null);
    return days;
  }, [cursor]);

  const eventsByDay = useMemo(() => {
    const map = {};
    for (const ev of events) {
      const k = localDateKey(ev.start_at);
      (map[k] = map[k] || []).push(ev);
    }
    return map;
  }, [events]);

  const today = localDateKey(new Date());
  const monthLabel = cursor.toLocaleDateString("it-IT", { month: "long", year: "numeric" });

  return (
    <div className="space-y-5" data-testid="agenda-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 mb-1">Pianificazione</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight flex items-center gap-2">
            <Calendar className="h-7 w-7 text-blue-600" /> Agenda
          </h1>
          <p className="text-sm text-slate-500 mt-1">Appuntamenti, telefonate e promemoria. Alert giornalieri e reminder per ogni evento.</p>
          {gcalConnected && (
            <p className="text-[11px] text-emerald-700 mt-1 inline-flex items-center gap-1" data-testid="gcal-sync-badge">
              🗓️ Sincronizzazione a due vie con Google Calendar attiva (pull automatico ogni 5 min)
            </p>
          )}
        </div>
        <div className="flex gap-2">
          {gcalConnected && (
            <Button variant="outline" onClick={pullFromGoogle} disabled={gcalPulling} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="agenda-pull-gcal">
              {gcalPulling ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
              Sincronizza da Google
            </Button>
          )}
          <Button variant="outline" onClick={() => setView(view === "month" ? "list" : "month")} data-testid="agenda-toggle-view">
            {view === "month" ? "Vista lista" : "Vista mese"}
          </Button>
          <Button onClick={() => openCreate(null)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="agenda-create">
            <Plus className="h-4 w-4 mr-1.5" /> Nuovo evento
          </Button>
        </div>
      </header>

      {/* Sezione subscribe iCal */}
      <Card className="border-emerald-200 bg-emerald-50/30">
        <CardContent className="py-3 flex flex-wrap items-center gap-3 text-xs">
          <Globe className="h-4 w-4 text-emerald-700" />
          <span className="font-medium text-emerald-900">Sincronizza con Google / Outlook / Apple:</span>
          {icalData?.ical_url ? (
            <>
              <code className="px-2 py-1 bg-white border border-emerald-200 rounded text-emerald-800 max-w-md truncate" data-testid="ical-url">{icalData.ical_url}</code>
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(icalData.ical_url); toast.success("URL copiato"); }} className="h-7 text-xs" data-testid="ical-copy">
                <Copy className="h-3 w-3 mr-1" /> Copia
              </Button>
              <Button size="sm" variant="outline" onClick={regenIcal} className="h-7 text-xs text-rose-600 border-rose-300" data-testid="ical-regen">Rigenera</Button>
              <a href={icalData.webcal_url} className="text-emerald-700 underline">Abbonati ora ↗</a>
            </>
          ) : (
            <Button size="sm" onClick={createIcalFirstTime} className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="ical-create">
              Crea link di sottoscrizione
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Navigatore */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} data-testid="agenda-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h2 className="font-display text-lg font-semibold capitalize min-w-[160px] text-center" data-testid="agenda-month-label">{monthLabel}</h2>
          <Button variant="outline" size="sm" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} data-testid="agenda-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setCursor(new Date())} className="text-xs">Oggi</Button>
        </div>
        <span className="text-xs text-slate-500">{events.length} eventi nel mese</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Caricamento…</div>
      ) : view === "month" ? (
        <div className="grid grid-cols-7 gap-px bg-slate-200 border border-slate-200 rounded-lg overflow-hidden" data-testid="agenda-month-grid">
          {["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"].map((d) => (
            <div key={d} className="bg-slate-50 px-2 py-1.5 text-[10px] uppercase font-semibold text-slate-600 text-center">{d}</div>
          ))}
          {monthGrid.map((d, i) => {
            const key = d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : `e-${i}`;
            const evs = d ? (eventsByDay[key] || []) : [];
            const isToday = key === today;
            return (
              <div key={i} className={`bg-white min-h-[100px] p-1.5 flex flex-col gap-1 ${d ? "cursor-pointer hover:bg-slate-50" : "bg-slate-50/50"} ${isToday ? "ring-2 ring-inset ring-blue-500 bg-blue-50/40" : ""}`} onClick={() => d && openCreate(d)} data-testid={d ? `agenda-day-${key}` : undefined}>
                {d && (
                  isToday ? (
                    <div className="flex items-center justify-between">
                      <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-blue-600 text-white text-xs font-bold shadow-sm" data-testid="agenda-today-badge">{d.getDate()}</span>
                      <span className="text-[9px] uppercase tracking-wider font-semibold text-blue-700">Oggi</span>
                    </div>
                  ) : (
                    <div className="text-xs font-semibold text-slate-700">{d.getDate()}</div>
                  )
                )}
                {evs.slice(0, 3).map((ev) => (
                  <button key={ev.id} onClick={(e) => { e.stopPropagation(); openEdit(ev); }} className={`text-left text-[10px] px-1.5 py-0.5 rounded border ${COLOR_CLASS[ev.color] || COLOR_CLASS.blue} truncate`} title={ev.title}>
                    {!ev.all_day && <span className="font-mono mr-1">{localTimeHM(ev.start_at)}</span>}
                    {ev.title}
                  </button>
                ))}
                {evs.length > 3 && <div className="text-[10px] text-slate-500">+{evs.length - 3} altri</div>}
              </div>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0 divide-y divide-slate-100">
            {events.length === 0 && <p className="text-sm text-slate-500 px-4 py-6 text-center">Nessun evento questo mese</p>}
            {events.map((ev) => (
              <button key={ev.id} onClick={() => openEdit(ev)} className="w-full text-left px-4 py-2.5 hover:bg-slate-50 flex items-start gap-3" data-testid={`agenda-row-${ev.id}`}>
                <div className={`w-1 h-12 rounded-full ${COLOR_CLASS[ev.color] || COLOR_CLASS.blue} border-0`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="font-medium text-sm truncate">{ev.title}</p>
                    <span className="text-[11px] tabular-nums text-slate-500 shrink-0">{formatDateTime(ev.start_at)}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 flex items-center gap-3 mt-0.5">
                    {ev.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{ev.location}</span>}
                    {ev.reminder_minutes_before && <span className="flex items-center gap-1"><Bell className="h-3 w-3" />{ev.reminder_minutes_before}m prima</span>}
                  </div>
                </div>
              </button>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Edit/Create Dialog */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-md" data-testid="agenda-event-dialog">
          <DialogHeader><DialogTitle>{edit?.id ? "Modifica evento" : "Nuovo evento"}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-2.5">
              <div>
                <Label className="text-xs">Titolo *</Label>
                <Input data-testid="agenda-title" value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} autoFocus />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Tipo</Label>
                  <select className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm bg-white" value={edit.type} onChange={(e) => { const t = TYPE_OPTIONS.find((x) => x.v === e.target.value); setEdit({ ...edit, type: e.target.value, color: t?.color || edit.color }); }} data-testid="agenda-type">
                    {TYPE_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Colore</Label>
                  <div className="flex gap-1 mt-2">
                    {["blue", "violet", "amber", "green", "rose", "slate"].map((c) => (
                      <button key={c} type="button" onClick={() => setEdit({ ...edit, color: c })} className={`h-6 w-6 rounded-full border-2 ${COLOR_CLASS[c]} ${edit.color === c ? "ring-2 ring-offset-1 ring-slate-700" : ""}`} aria-label={c} />
                    ))}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Inizio *</Label>
                  <DateTimePicker data-testid="agenda-start" value={edit.start_at} onChange={(e) => setEdit({ ...edit, start_at: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Fine</Label>
                  <DateTimePicker data-testid="agenda-end" value={edit.end_at} onChange={(e) => setEdit({ ...edit, end_at: e.target.value })} />
                </div>
              </div>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={edit.all_day} onChange={(e) => setEdit({ ...edit, all_day: e.target.checked })} className="h-3.5 w-3.5" data-testid="agenda-all-day" />
                <span>Tutto il giorno</span>
              </label>
              <div>
                <Label className="text-xs">Luogo</Label>
                <Input data-testid="agenda-location" value={edit.location} onChange={(e) => setEdit({ ...edit, location: e.target.value })} placeholder="Ufficio, indirizzo, link videocall..." />
              </div>
              <div>
                <Label className="text-xs">Reminder</Label>
                <select className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm bg-white" value={edit.reminder_minutes_before === null || edit.reminder_minutes_before === undefined ? "" : String(edit.reminder_minutes_before)} onChange={(e) => setEdit({ ...edit, reminder_minutes_before: e.target.value === "" ? "" : Number(e.target.value) })} data-testid="agenda-reminder">
                  {REMINDER_OPTIONS.map((o) => <option key={String(o.v)} value={o.v}>{o.l}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Note</Label>
                <Textarea data-testid="agenda-description" rows={2} value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter className="flex-wrap gap-2">
            {edit?.id && <Button variant="outline" onClick={remove} disabled={busy} className="text-rose-600 border-rose-300 mr-auto" data-testid="agenda-delete"><Trash2 className="h-4 w-4 mr-1" /> Elimina</Button>}
            <Button variant="outline" onClick={() => setEdit(null)}>Annulla</Button>
            <Button onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="agenda-save">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Dialog OTP per rigenerazione iCal */}
      <Dialog open={otpOpen} onOpenChange={(o) => { if (!o) { setOtpOpen(false); setOtpStep("idle"); setOtpCode(""); } }}>
        <DialogContent className="max-w-sm" data-testid="agenda-otp-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-800">
              <Bell className="h-5 w-5 text-amber-600" />
              Verifica rigenerazione link
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 -mt-1">
            Per sicurezza, conferma con il codice OTP inviato alla tua email. Il vecchio link smetterà subito di funzionare.
          </p>
          {otpStep === "sent" && otpRecipient && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2.5 text-xs text-emerald-800">
              Codice inviato a <b>{otpRecipient}</b>. Valido per 15 minuti.
            </div>
          )}
          <div>
            <Label className="text-xs">Codice OTP (6 cifre)</Label>
            <Input
              data-testid="agenda-otp-code"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              className="text-center tracking-[0.4em] font-mono text-lg"
              maxLength={6}
              inputMode="numeric"
              autoFocus
            />
          </div>
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={requestRegenOtp} disabled={otpBusy} data-testid="agenda-otp-resend" className="text-xs">
              {otpStep === "sent" ? "Reinvia codice" : "Invia codice"}
            </Button>
            <Button onClick={verifyAndRegen} disabled={otpBusy || otpCode.length !== 6} data-testid="agenda-otp-verify" className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {otpBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Verifica e rigenera
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
